import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useFetch from '../../hooks/useFetch';
import { useAuth } from '../../hooks/useAuth';
import { useUnitats } from '../../hooks/useUnitats';
import { extractErrorMessage } from '../../utils/errorHandling';
import {
	ErrorMessage,
	LoadingGif,
	WideContentContainer,
} from '../Shared/CommonStyles';
import { axiosInstance } from '../../config/axios';

import * as S from './YearSuggestionsOverview.styles';

// ─── Types ─────────────────────────────────────────────────────────────────

interface YearBreakdownEntry {
	schoolYear: string;
	count: number;
}

interface AlbumOverview {
	albumId: string;
	albumName: string;
	unitat: string;
	uploadDate: string;
	pictureCount?: number;
	totalSuggestions: number;
	yearBreakdown: YearBreakdownEntry[];
	topYear: YearBreakdownEntry | null;
}

interface OverviewResponse {
	albums: AlbumOverview[];
	totalAlbums: number;
	totalSuggestions: number;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

const getUnitatDisplayName = (
	unitat: string,
	unitats: Array<{ abreviacio: string; nom: string }> | undefined | null,
): string => {
	if (unitat === 'agrupament') return 'Agrupament';
	const found = unitats?.find((u) => u.abreviacio.toLowerCase() === unitat);
	return found?.nom || unitat;
};

// Build the full year list for the apply dropdown (2000-01 → current)
const generateSchoolYears = (): string[] => {
	const years: string[] = [];
	const now = new Date();
	const startYear = now.getMonth() >= 8 ? now.getFullYear() : now.getFullYear() - 1;
	for (let y = startYear; y >= 2000; y--) {
		years.push(`${y}-${y + 1}`);
	}
	return years;
};

// ─── Component ─────────────────────────────────────────────────────────────

const YearSuggestionsOverview: React.FC = () => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { user } = useAuth();
	const { unitats } = useUnitats();
	const allYears = useMemo(() => generateSchoolYears(), []);

	const { data, isLoading, error } = useFetch<OverviewResponse>({
		queryKey: ['year-suggestions-overview'],
		url: '/api/galeria/albums/year-suggestions-overview',
	});

	const [applying, setApplying] = useState<string | null>(null);
	const [applyError, setApplyError] = useState<string | null>(null);
	// per-album chosen year for the apply dropdown (defaults to topYear)
	const [chosenYear, setChosenYear] = useState<Record<string, string>>({});

	const isAllowed =
		!!user &&
		(user.roles.includes('Admin') || user.roles.includes('Agrupament'));

	if (!isAllowed) {
		return (
			<WideContentContainer>
				<ErrorMessage>No tens permís per veure aquesta pàgina.</ErrorMessage>
			</WideContentContainer>
		);
	}

	const handleApply = async (albumId: string) => {
		const year = chosenYear[albumId];
		if (!year) return;
		setApplying(albumId);
		setApplyError(null);
		try {
			await axiosInstance.post(`/api/galeria/albums/${albumId}/apply-year`, {
				schoolYear: year,
			});
			queryClient.invalidateQueries({
				queryKey: ['year-suggestions-overview'],
			});
		} catch (err: any) {
			setApplyError(
				err?.response?.data?.error || 'Error en assignar el curs',
			);
		} finally {
			setApplying(null);
		}
	};

	const albums = data?.albums || [];
	const sortedAlbums = useMemo(
		() => [...albums].sort((a, b) => b.totalSuggestions - a.totalSuggestions),
		[albums],
	);

	return (
		<WideContentContainer>
			<S.Container>
				<S.HeaderRow>
					<div>
						<S.Title>Suggeriments de curs</S.Title>
						<S.Subtitle>
							Àlbums amb unitat coneguda però curs desconegut on la gent ha suggerit un any
						</S.Subtitle>
					</div>
					<S.BackButton onClick={() => navigate('/galeria')}>
						← Tornar a la galeria
					</S.BackButton>
				</S.HeaderRow>

				{isLoading && <LoadingGif size="large" />}

				{error && !isLoading && (
					<ErrorMessage>Error: {extractErrorMessage(error)}</ErrorMessage>
				)}

				{applyError && <ErrorMessage>{applyError}</ErrorMessage>}

				{!isLoading && !error && (
					<>
						<S.SummaryGrid>
							<S.SummaryCard>
								<S.SummaryLabel>Àlbums amb suggeriments</S.SummaryLabel>
								<S.SummaryValue>{data?.totalAlbums ?? 0}</S.SummaryValue>
							</S.SummaryCard>
							<S.SummaryCard>
								<S.SummaryLabel>Total suggeriments</S.SummaryLabel>
								<S.SummaryValue>{data?.totalSuggestions ?? 0}</S.SummaryValue>
							</S.SummaryCard>
						</S.SummaryGrid>

						{sortedAlbums.length === 0 ? (
							<S.EmptyState>
								📭 Encara no hi ha suggeriments de curs.
								<div style={{ marginTop: 8, fontSize: '0.9rem' }}>
									Quan algú suggeriixi un any per a un àlbum amb curs desconegut, apareixerà aquí.
								</div>
							</S.EmptyState>
						) : (
							<S.AlbumList>
								{sortedAlbums.map((album) => {
									const maxCount = Math.max(
										...album.yearBreakdown.map((y) => y.count),
										1,
									);
									const selected =
										chosenYear[album.albumId] ||
										album.topYear?.schoolYear ||
										'';
									return (
										<S.AlbumCard key={album.albumId}>
											<S.AlbumTop>
												<S.AlbumName>{album.albumName}</S.AlbumName>
												<S.UnitatBadge>
													{getUnitatDisplayName(album.unitat, unitats)}
												</S.UnitatBadge>
											</S.AlbumTop>
											<S.Meta>
												<span>
													📅{' '}
													{new Date(album.uploadDate).toLocaleDateString(
														'ca-ES',
														{ year: 'numeric', month: 'long', day: 'numeric' },
													)}
												</span>
												{album.pictureCount != null && (
													<span>🖼️ {album.pictureCount} fotos</span>
												)}
												<span>
													📋 {album.totalSuggestions}{' '}
													{album.totalSuggestions === 1
														? 'suggeriment'
														: 'suggeriments'}
												</span>
											</S.Meta>

											{album.yearBreakdown.map((y) => (
												<S.YearRow key={y.schoolYear}>
													<S.YearLabel>{y.schoolYear}</S.YearLabel>
													<S.BarTrack>
														<S.BarFill $width={(y.count / maxCount) * 100} />
													</S.BarTrack>
													<S.YearCount>
														{y.count}{' '}
														{y.count === 1 ? 'vot' : 'vots'}
													</S.YearCount>
												</S.YearRow>
											))}

											<S.ApplyRow>
												<S.YearSelect
													value={selected}
													onChange={(e) =>
														setChosenYear((prev) => ({
															...prev,
															[album.albumId]: e.target.value,
														}))
													}
												>
													{allYears.map((yr) => (
														<option key={yr} value={yr}>
															{yr}
														</option>
													))}
												</S.YearSelect>
												<S.ApplyButton
													onClick={() => handleApply(album.albumId)}
													disabled={applying === album.albumId || !selected}
												>
													{applying === album.albumId
														? 'Assignant...'
														: 'Assigna curs'}
												</S.ApplyButton>
												<S.ViewLink
													onClick={() =>
														navigate(`/galeria/${album.unitat}/${album.albumId}`)
													}
												>
													Veure àlbum
												</S.ViewLink>
											</S.ApplyRow>
										</S.AlbumCard>
									);
								})}
							</S.AlbumList>
						)}
					</>
				)}
			</S.Container>
		</WideContentContainer>
	);
};

export default YearSuggestionsOverview;